import { makeStyles } from '@material-ui/styles';
import { Spin } from 'antd';
import PropTypes from 'prop-types';
import React, { useRef, useCallback } from 'react';
import useScrollReachBottom from 'common/hooks/useScrollReachBottom';
import SvgFormat from 'common/helpers/svgFormat';
import PendingVerificationCard from './pending-card';
import BlankPage from '../blank-page';

const useStyles = makeStyles(
  theme => ({
    root: {
      height: 'calc(100vh - 224px)',
      overflowY: 'auto',
      marginTop: 24,
    },
    list: {
      '& > *:not(:first-child)': {
        width: '100%',
        marginTop: 20,
      },
    },
    noPendingSvg: {
      marginBottom: 32,
    },
    loading: {
      ...theme.flex.center,
      position: 'absolute',
      zIndex: 1,
      opacity: 0.8,
      width: '100%',
      height: 'calc(100vh - 224px)',
      backgroundColor: theme.colors.gray1,
    },
    bottomLoading: {
      width: '100%',
      marginTop: 8,
    },
  }),
  { name: 'pendingVerification' },
);

const Loading = () => {
  const classes = useStyles();
  return (
    <div className={classes.loading}>
      <Spin />
    </div>
  );
};

const PendingVerification = ({
  landlords,
  loading,
  fetchMore,
  refetchLandlords,
  queryLandlordsCalled,
  pendingPageNumber,
  setPendingPageNumber,
}) => {
  const ref = useRef();
  const classes = useStyles();

  const landlordList = landlords?.dataSource ?? [];
  const pageInfo = landlords?.pageInfo;

  const handleReachBottom = useCallback(async () => {
    const newPageNumber = pendingPageNumber + 1;
    setPendingPageNumber(newPageNumber);

    await fetchMore({
      variables: {
        pageNumber: newPageNumber,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          ...prev,
          ...fetchMoreResult,
          landlords: {
            ...prev?.landlords,
            ...fetchMoreResult?.landlords,
            edges: [...prev?.landlords?.edges, ...fetchMoreResult?.landlords?.edges],
            pageInfo: {
              ...fetchMoreResult?.landlords?.pageInfo,
            },
          },
        };
      },
    });
  }, [fetchMore, pendingPageNumber, setPendingPageNumber]);

  useScrollReachBottom(
    () => ref?.current,
    handleReachBottom,
    !loading && pendingPageNumber < pageInfo?.numPages && landlordList?.length < landlords?.total,
  );

  const refetchLandlordsAndResetPageNumber = () => {
    refetchLandlords();
    setPendingPageNumber(1);
  };

  return (
    <>
      <Choose>
        <When condition={landlordList?.length < 1 && !loading}>
          <BlankPage
            svg={<SvgFormat type="no-filters" className={classes.noPendingSvg} />}
            descKey="cms.landlords.blank_page.no_pending"
          />
        </When>
        <Otherwise>
          <If condition={loading && pendingPageNumber === 1}>
            <Loading />
          </If>
          <div ref={ref} className={classes.root}>
            <div className={classes.list}>
              {landlordList.map((landlordInfo, index) => (
                <div key={`${landlordInfo?.id}-${index}`}>
                  <PendingVerificationCard
                    landlordInfo={landlordInfo}
                    refetchLandlords={refetchLandlordsAndResetPageNumber}
                  />
                </div>
              ))}
            </div>
            {loading && queryLandlordsCalled && pendingPageNumber > 1 && (
              <Spin className={classes.bottomLoading} />
            )}
          </div>
        </Otherwise>
      </Choose>
    </>
  );
};

PendingVerification.propTypes = {
  landlords: PropTypes.object,
  loading: PropTypes.bool,
  fetchMore: PropTypes.func,
  refetchLandlords: PropTypes.func,
  queryLandlordsCalled: PropTypes.bool,
  pendingPageNumber: PropTypes.number,
  setPendingPageNumber: PropTypes.func,
};

export default PendingVerification;
